import { FC, useEffect, useState } from "react";
import { Empty, message, Segmented, Skeleton, Tabs, TabsProps } from "antd";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { InfoCircleOutlined } from "@ant-design/icons";
import useCommonStore from "stores/useCommon";

import API from "services/api";
import _ from "lodash";

const KEY_TAB = {
  DPE: "dpe",
  CO2: "co2",
};

const toIsoDate = (value: string) => {
  if (!value) {
    return "";
  }

  const parts = value.split("/");
  if (parts.length !== 3) {
    return "";
  }

  const [day, month, year] = parts;
  if (!day || !month || !year) {
    return "";
  }

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// Electricity conversion factor used to draw the label (svg_result new_coeff)
type CoeffOption = "before_2026" | "2026" | "2027";

const COEFF_OPTIONS: { value: CoeffOption; label: string; text: string }[] = [
  { value: "before_2026", label: "< 2026", text: "Etiquette d'origine (ancien facteur de conversation de l'électricité)" },
  { value: "2026", label: "2026", text: "Etiquette mise à jour (facteur 2026 de conversion de l'électricité)" },
  { value: "2027", label: "2027", text: "Etiquette simulée en intégrant le facteur 2027 de conversion de l'électricité (01/01/2027)" },
];

// "< 2026" sends no new_coeff; 2026 / 2027 are sent as the year
const getCoeffParams = (coeff: CoeffOption) =>
  coeff === "before_2026" ? {} : { new_coeff: coeff };


const ResultatTab: FC = ({ dataDynamicInfo, dataHomeUploaded }: any) => {
  const { id } = useParams();
  const [messageApi] = message.useMessage();
  // const [dataHomeUploaded, setDataHomeUploaded] = useState(null);
  const [ResultatDPE, setResultatDPE] = useState(null);
  const [ResultatCO2, setResultatCO2] = useState(null);
  const [isLoadingDPE, setIsLoadingDPE] = useState(true);
  const [isLoadingCO2, setIsLoadingCO2] = useState(true);
  const [coeff, setCoeff] = useState<CoeffOption>("2026");
  // const maxDate = process.env.REACT_APP_MAX_DATE || "2026-01-01";
  const maxDate = process.env.REACT_APP_MAX_DATE || "1767225600";
  // console.log("MAX_DATE raw:", process.env.REACT_APP_MAX_DATE);
  // console.log(
  //   "MAX_DATE final:",
  //   maxDate,
  //   "parsed:",
  //   parseInt(maxDate, 10),
  //   "done_on:",
  //   _.get(dataHomeUploaded, "dpe_details.done_on.1")
  // );
  const coeffText = COEFF_OPTIONS.find((o) => o.value === coeff)?.text;
  // "< 2026" (old factor) only applies to DPEs established before REACT_APP_MAX_DATE (01/01/2026)
  const isDoneBeforeMaxDate =
    !!maxDate &&
    _.get(dataHomeUploaded, "dpe_details.done_on.1") < parseInt(maxDate);
  const coeffOptions = COEFF_OPTIONS.filter(
    (o) => o.value !== "before_2026" || isDoneBeforeMaxDate
  );

  const { setDynamicInfoKey, setIsShowModalDynamicInfo } = useCommonStore();

  const [tab, setTab] = useState(KEY_TAB.DPE);

  const { mutateAsync: mutateDPE, isSuccess: isSuccessDPE } = useMutation({
    mutationFn: (params: any) => API.mock.getResultat(params),
    retry: 0,
  });

  const { mutateAsync: mutateCO2, isSuccess: isSuccessCO2 } = useMutation({
    mutationFn: (params: any) => API.mock.getResultat(params),
    retry: 0,
  });

  useEffect(() => {
    loadData(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // reload both tabs when the factor changes to avoid stale SVGs
    setIsLoadingDPE(true);
    setIsLoadingCO2(true);
    // clear previous results so switching tabs doesn't reuse stale data
    setResultatDPE(null);
    setResultatCO2(null);
    // fetch both in parallel
    loadData(KEY_TAB.DPE);
    loadData(KEY_TAB.CO2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coeff]);

  const handleChangeTab = (value: string) => {
    setTab(value);

    if (value === KEY_TAB.CO2 && !ResultatCO2) {
      loadData(value);
    }
  };

  const handleViewInfo = (key: string) => {
    setDynamicInfoKey(key);
    setIsShowModalDynamicInfo(true);
  };

  const loadData = async (currentTab: string) => {
    try {
      if (currentTab === KEY_TAB.DPE) {
        const resultSVG = await mutateDPE({
          id,
          type: currentTab,
          ...getCoeffParams(coeff),
        });
        setResultatDPE(resultSVG);
      } else {
        const resultSVG = await mutateCO2({
          id,
          type: currentTab,
          ...getCoeffParams(coeff),
        });
        setResultatCO2(resultSVG);
      }
    } catch (e: any) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
        duration: 2,
      });
    } finally {
      if (currentTab === KEY_TAB.DPE) {
        setIsLoadingDPE(false);
      } else {
        setIsLoadingCO2(false);
      }
    }
  };

  const tabItems: TabsProps["items"] = [
    {
      key: KEY_TAB.DPE,
      label: (
        <span>
          En kWh/m<sup>2</sup>
        </span>
      ),
      children: (
        <div className="text-center">
          {isLoadingDPE ? (
            <div className="svg-image mx-auto">
              <Skeleton.Button active />
            </div>
          ) : isSuccessDPE && ResultatDPE ? (
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(
                ResultatDPE || ""
              )}`}
              alt="result"
              className="mx-auto"
              style={{ maxHeight: "300px" }}
            />
          ) : isSuccessDPE && !ResultatDPE ? (
            <Empty imageStyle={{ height: 310, marginBottom: 0 }} />
          ) : null}
        </div>
      ),
    },

    {
      key: KEY_TAB.CO2,
      label: (
        <span>
          En CO<sub>2</sub>/m<sup>2</sup>
        </span>
      ),
      children: (
        <div className="text-center">
          {isLoadingCO2 ? (
            <div className="svg-image mx-auto">
              <Skeleton.Button active />
            </div>
          ) : isSuccessCO2 && ResultatCO2 ? (
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(
                ResultatCO2 || ""
              )}`}
              alt="result"
              className="mx-auto"
              style={{ maxHeight: "300px" }}
            />
          ) : isSuccessCO2 && !ResultatCO2 ? (
            <Empty imageStyle={{ height: 310, marginBottom: 0 }} />
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="energy-home__resultats">
      <Tabs
        centered
        activeKey={tab}
        items={tabItems}
        onChange={handleChangeTab}
      />

      <div className="flex flex-col gap-1 w-[265px] mx-auto mt-2">
        {_.get(dataDynamicInfo, "under_40sqm.title") && (
          <div className="info-resultat-item flex justify-start items-center gap-2">
            <span>{dataDynamicInfo["under_40sqm"]?.title}</span>
            <InfoCircleOutlined onClick={() => handleViewInfo("under_40sqm")} />
          </div>
        )}
        {_.get(dataDynamicInfo, "maximised_GES.title") && (
          <div className="info-resultat-item flex justify-start items-center gap-2">
            <span>{dataDynamicInfo["maximised_GES"]?.title}</span>
            <InfoCircleOutlined
              onClick={() => handleViewInfo("maximised_GES")}
            />
          </div>
        )}
        <div className="info-resultat-item flex flex-col items-start gap-1 mt-2 print-hidden">
          <Segmented<CoeffOption>
            size="small"
            block
            value={coeff}
            onChange={setCoeff}
            options={coeffOptions.map(({ value, label }) => ({ value, label }))}
            className="w-full"
          />
          <div className="flex justify-start items-start gap-2">
            <span className="leading-5">{coeffText}</span>
            <InfoCircleOutlined onClick={() => handleViewInfo("new_coeff")} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultatTab;
